import type {
  ArcturianActionRecord,
  ArcturianConversationEvent,
  ArcturianConversationEventsResponse,
  ArcturianEventPresentation,
} from '../api/client';

const ACTION_PROTOCOL = 'agentos.arcturian-action.v1';
const CURSOR_VERSION = 1;
const CURSOR_PREFIX = 'cloud-arcturian-return-v1';
const DEVICE_KEY = 'cloud-arcturian-return-device';
const MAX_TRACKED_EVENTS = 200;
const DEFAULT_POLL_MS = 5000;
const DEFAULT_PAGE_SIZE = 100;
const RETURN_RESCUE_TAIL_SIZE = 500;
const MAX_NARRATION_SOURCE_EVENTS = 20;
const MAX_NARRATION_SOURCE_CHARS = 6000;
export const ARCTURIAN_RETURN_MAX_SPOKEN_CHARS = 400;

export interface ArcturianActionPointer {
  actionId: string;
  conversationId: string;
  principalUserId: string;
  updatedAt: number;
}

/** Baseline is intentionally a replaceable boundary. Phase 1 reads the
 * revision contained in AiApi's preface; Phase 2 can replace this source with
 * a server-owned preface boundary without changing cursor or polling logic. */
export interface ArcturianReturnBaselineSource {
  revision(): number | null;
}

export class PrefaceReturnBaselineAdapter implements ArcturianReturnBaselineSource {
  constructor(private readonly throughRevision: number | null) {}

  revision(): number | null {
    return Number.isInteger(this.throughRevision) && Number(this.throughRevision) >= 1
      ? Number(this.throughRevision)
      : null;
  }
}

export interface ArcturianReturnApi {
  listEvents(
    conversationId: string,
    afterRevision: number,
    limit?: number,
    tail?: number,
  ): Promise<ArcturianConversationEventsResponse>;
  acknowledgePresentation(
    conversationId: string,
    eventId: string,
    body: { device_id: string; status: 'pending' | 'presented' | 'consumed' },
  ): Promise<{ presentation: ArcturianEventPresentation }>;
}

interface PendingMarker {
  eventId: string;
  revision: number;
}

interface ReturnCursorState {
  version: 1;
  acceptedRevision: number;
  pending: PendingMarker[];
  projectedEventIds: string[];
}

export interface ArcturianReturnCursorStore {
  read(principalUserId: string, conversationId: string, deviceId: string): ReturnCursorState | null;
  write(
    principalUserId: string,
    conversationId: string,
    deviceId: string,
    state: ReturnCursorState,
  ): void;
}

function cursorKey(principalUserId: string, conversationId: string, deviceId: string): string {
  return [CURSOR_PREFIX, principalUserId, conversationId, deviceId]
    .map((part) => encodeURIComponent(part))
    .join(':');
}

function validCursor(value: unknown): ReturnCursorState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const item = value as Record<string, unknown>;
  if (item.version !== CURSOR_VERSION
      || !Number.isInteger(item.acceptedRevision) || Number(item.acceptedRevision) < 0
      || !Array.isArray(item.pending)
      || !Array.isArray(item.projectedEventIds)) return null;
  const pending = item.pending.flatMap((raw): PendingMarker[] => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
    const marker = raw as Record<string, unknown>;
    return typeof marker.eventId === 'string' && marker.eventId
      && Number.isInteger(marker.revision) && Number(marker.revision) >= 1
      ? [{ eventId: marker.eventId, revision: Number(marker.revision) }]
      : [];
  }).slice(-MAX_TRACKED_EVENTS);
  const projectedEventIds = item.projectedEventIds
    .filter((eventId): eventId is string => typeof eventId === 'string' && Boolean(eventId))
    .slice(-MAX_TRACKED_EVENTS);
  return {
    version: CURSOR_VERSION,
    acceptedRevision: Number(item.acceptedRevision),
    pending,
    projectedEventIds,
  };
}

export class LocalArcturianReturnCursorStore implements ArcturianReturnCursorStore {
  read(principalUserId: string, conversationId: string, deviceId: string): ReturnCursorState | null {
    try {
      const raw = localStorage.getItem(cursorKey(principalUserId, conversationId, deviceId));
      return raw ? validCursor(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  write(
    principalUserId: string,
    conversationId: string,
    deviceId: string,
    state: ReturnCursorState,
  ): void {
    try {
      localStorage.setItem(cursorKey(principalUserId, conversationId, deviceId), JSON.stringify(state));
    } catch {
      // Device state is an optimisation. Server conversation ownership and
      // events remain authoritative when browser storage is unavailable.
    }
  }
}

export function arcturianReturnDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_KEY)?.trim();
    if (existing) return existing;
    const created = `web_${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_KEY, created);
    return created;
  } catch {
    return `web_ephemeral_${crypto.randomUUID()}`;
  }
}

function actionPointer(action: ArcturianActionRecord): ArcturianActionPointer | null {
  if (action?.protocol !== ACTION_PROTOCOL
      || typeof action.action_id !== 'string' || !action.action_id.trim()
      || typeof action.conversation_id !== 'string' || !action.conversation_id.trim()
      || typeof action.principal_user_id !== 'string' || !action.principal_user_id.trim()
      || typeof action.updated_at !== 'number' || !Number.isFinite(action.updated_at)) return null;
  return {
    actionId: action.action_id,
    conversationId: action.conversation_id,
    principalUserId: action.principal_user_id,
    updatedAt: action.updated_at,
  };
}

/** Select only from the JWT-filtered server projection; never infer ownership
 * from an email, target name or a browser-stored conversation id. */
export function latestArcturianActionPointer(
  actions: readonly ArcturianActionRecord[],
  preferredConversationId?: string,
): ArcturianActionPointer | null {
  const candidates = actions.flatMap((action) => {
    const pointer = actionPointer(action);
    return pointer ? [pointer] : [];
  });
  const preferred = preferredConversationId?.trim();
  const pool = preferred
    ? candidates.filter((candidate) => candidate.conversationId === preferred)
    : candidates;
  return [...pool].sort((a, b) => b.updatedAt - a.updatedAt
    || b.actionId.localeCompare(a.actionId))[0] || null;
}

function validateEventsResponse(
  value: ArcturianConversationEventsResponse,
  pointer: ArcturianActionPointer,
  afterRevision: number,
): ArcturianConversationEvent[] {
  if (!value || value.conversation_id !== pointer.conversationId
      || !Array.isArray(value.events)
      || !Number.isInteger(value.next_revision)
      || value.next_revision < afterRevision) {
    throw new Error('invalid_arcturian_return_page');
  }
  let prior = afterRevision;
  return value.events.map((event) => {
    const valid = event && event.conversation_id === pointer.conversationId
      && event.principal_user_id === pointer.principalUserId
      && typeof event.event_id === 'string' && Boolean(event.event_id)
      && Number.isInteger(event.revision) && event.revision > prior
      && (event.direction === 'inbound' || event.direction === 'outbound')
      && typeof event.actor === 'string'
      && typeof event.event_type === 'string'
      && typeof event.content === 'string'
      && Array.isArray(event.artifacts);
    if (!valid) throw new Error('invalid_arcturian_return_event');
    prior = event.revision;
    return event;
  });
}

export interface ArcturianReturnBatch {
  events: ArcturianConversationEvent[];
  catchUp: boolean;
}

interface ArcturianReturnControllerOptions {
  deviceId: string;
  baseline: ArcturianReturnBaselineSource;
  cursorStore?: ArcturianReturnCursorStore;
  pollMs?: number;
  pageSize?: number;
  onPending: (batch: ArcturianReturnBatch) => void;
  onError?: (error: Error) => void;
}

/** Polls the durable owner-scoped action conversation. Accepted and projected
 * cursors are deliberately separate: a STOP after fetch but before speech must
 * leave the reply pending. Pending content is re-fetched from the server; only
 * event ids/revisions are device state, never a second conversation truth. */
export class ArcturianReturnController {
  private readonly store: ArcturianReturnCursorStore;
  private state: ReturnCursorState;
  private readonly pendingEvents = new Map<string, ArcturianConversationEvent>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private polling = false;
  private firstPoll = true;
  private seedAtCurrentTip: boolean;
  private readonly sessionActionIds = new Set<string>();
  private readonly rescueActionIds = new Set<string>();

  constructor(
    private readonly api: ArcturianReturnApi,
    readonly pointer: ArcturianActionPointer,
    private readonly options: ArcturianReturnControllerOptions,
  ) {
    this.store = options.cursorStore || new LocalArcturianReturnCursorStore();
    const saved = this.store.read(pointer.principalUserId, pointer.conversationId, options.deviceId);
    const baseline = options.baseline.revision();
    this.seedAtCurrentTip = !saved && baseline == null;
    this.state = saved || {
      version: CURSOR_VERSION,
      acceptedRevision: baseline ?? 0,
      pending: [],
      projectedEventIds: [],
    };
    // Without a trustworthy baseline, revision 0 is only an in-memory probe.
    // Persisting it before the first successful tip read would make a STOP or
    // network error turn the next start into an accidental full-history replay.
    if (!saved && !this.seedAtCurrentTip) this.persist();
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.poll();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  pending(): ArcturianConversationEvent[] {
    return [...this.pendingEvents.values()].sort((a, b) => a.revision - b.revision);
  }

  /** Marks an action created by this live browser session as safe to recover
   * across the initial tip seed. Discovery pointers are intentionally not
   * registered: an old latest action must never turn foreign history into a
   * current spoken result. */
  registerSessionAction(actionId: string): void {
    const canonical = actionId.trim();
    if (!canonical || this.sessionActionIds.has(canonical)) return;
    this.sessionActionIds.add(canonical);
    this.rescueActionIds.add(canonical);
    if (this.stopped || this.polling) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    void this.poll();
  }

  async poll(): Promise<void> {
    if (this.stopped || this.polling) return;
    this.polling = true;
    const catchUp = this.firstPoll;
    try {
      const oldestPending = this.state.pending
        .reduce((lowest, marker) => Math.min(lowest, marker.revision), Number.POSITIVE_INFINITY);
      const afterRevision = Number.isFinite(oldestPending)
        ? Math.max(0, oldestPending - 1)
        : this.state.acceptedRevision;
      // Snapshot the rescue set before the request. An action registered while
      // this fetch is in flight stays queued for a second pass instead of being
      // incorrectly considered covered by a response that may predate it.
      const rescueIds = new Set(this.rescueActionIds);
      const scanRecent = this.seedAtCurrentTip || rescueIds.size > 0;
      const response = scanRecent
        ? await this.api.listEvents(
          this.pointer.conversationId,
          0,
          RETURN_RESCUE_TAIL_SIZE,
          RETURN_RESCUE_TAIL_SIZE,
        )
        : await this.api.listEvents(
          this.pointer.conversationId,
          afterRevision,
          this.options.pageSize ?? DEFAULT_PAGE_SIZE,
        );
      const events = validateEventsResponse(response, this.pointer, scanRecent ? 0 : afterRevision);
      if (scanRecent) {
        // No baseline means the client cannot distinguish heard from unheard
        // history. Fail forward to the current tip, but retain inbound events
        // that belong to actions explicitly created by THIS live session. This
        // closes the one-second race where POST /actions completes before the
        // first tip read and would otherwise swallow the awaited result.
        const projected = new Set(this.state.projectedEventIds);
        const pending = new Map(this.state.pending.map((marker) => [marker.eventId, marker]));
        for (const event of events) {
          if (event.direction !== 'inbound'
              || !event.action_id
              || !rescueIds.has(event.action_id)
              || projected.has(event.event_id)) continue;
          pending.set(event.event_id, { eventId: event.event_id, revision: event.revision });
          this.pendingEvents.set(event.event_id, event);
        }
        this.state.pending = [...pending.values()]
          .sort((a, b) => a.revision - b.revision)
          .slice(-MAX_TRACKED_EVENTS);
        this.state.acceptedRevision = Math.max(
          this.state.acceptedRevision,
          response.next_revision,
          events[events.length - 1]?.revision ?? 0,
        );
        for (const actionId of rescueIds) this.rescueActionIds.delete(actionId);
        this.seedAtCurrentTip = false;
        this.firstPoll = false;
        this.persist();
        const ready = this.pending();
        if (ready.length) this.options.onPending({ events: ready, catchUp: false });
        return;
      }
      const projected = new Set(this.state.projectedEventIds);
      const pending = new Map(this.state.pending.map((marker) => [marker.eventId, marker]));
      for (const event of events) {
        this.state.acceptedRevision = Math.max(this.state.acceptedRevision, event.revision);
        if (event.direction !== 'inbound' || projected.has(event.event_id)) continue;
        pending.set(event.event_id, { eventId: event.event_id, revision: event.revision });
        this.pendingEvents.set(event.event_id, event);
      }
      this.state.pending = [...pending.values()]
        .sort((a, b) => a.revision - b.revision)
        .slice(-MAX_TRACKED_EVENTS);
      this.persist();
      const ready = this.pending();
      if (ready.length) this.options.onPending({ events: ready, catchUp });
      this.firstPoll = false;
    } catch (cause) {
      this.options.onError?.(cause instanceof Error ? cause : new Error('arcturian_return_poll_failed'));
    } finally {
      this.polling = false;
      if (!this.stopped) {
        this.timer = setTimeout(
          () => { void this.poll(); },
          this.rescueActionIds.size ? 0 : this.options.pollMs ?? DEFAULT_POLL_MS,
        );
      }
    }
  }

  markProjected(events: readonly ArcturianConversationEvent[]): void {
    if (!events.length) return;
    const ids = new Set(events.map((event) => event.event_id));
    this.state.pending = this.state.pending.filter((marker) => !ids.has(marker.eventId));
    this.state.projectedEventIds = [
      ...this.state.projectedEventIds.filter((eventId) => !ids.has(eventId)),
      ...ids,
    ].slice(-MAX_TRACKED_EVENTS);
    for (const eventId of ids) this.pendingEvents.delete(eventId);
    this.persist();
    for (const event of events) {
      void this.api.acknowledgePresentation(this.pointer.conversationId, event.event_id, {
        device_id: this.options.deviceId,
        status: 'consumed',
      }).catch(() => { /* local spoken cursor remains authoritative per device */ });
    }
  }

  private persist(): void {
    this.store.write(
      this.pointer.principalUserId,
      this.pointer.conversationId,
      this.options.deviceId,
      this.state,
    );
  }
}

export interface ArcturianReturnNarrationPlan {
  events: ArcturianConversationEvent[];
  sourceEvents: ArcturianConversationEvent[];
  instruction: string;
}

/** One bounded spoken response per delivery window. Individual cards/server
 * events retain full detail; the voice receives at most a compact source set. */
export function planArcturianReturnNarration(
  events: readonly ArcturianConversationEvent[],
  catchUp: boolean,
): ArcturianReturnNarrationPlan {
  const ordered = [...events].sort((a, b) => a.revision - b.revision);
  const sourceEvents = ordered.slice(-MAX_NARRATION_SOURCE_EVENTS).map((event) => ({
    ...event,
    content: event.content.slice(0, Math.max(
      120,
      Math.floor(MAX_NARRATION_SOURCE_CHARS / Math.max(1, Math.min(ordered.length, MAX_NARRATION_SOURCE_EVENTS))),
    )),
  }));
  const omitted = ordered.length - sourceEvents.length;
  const instruction = catchUp || ordered.length > 1
    ? `Fasse ${ordered.length} eingetroffene Agenten-Rückmeldungen gemeinsam in höchstens ${ARCTURIAN_RETURN_MAX_SPOKEN_CHARS} Zeichen und maximal drei Sätzen zusammen. Nenne Ergebnisse und offene Rückfragen, keine technischen Metadaten.${omitted > 0 ? ` ${omitted} ältere Details bleiben in den Karten.` : ''}`
    : eventNeedsSummary(ordered[0])
      ? `Fasse diese Agenten-Rückmeldung in höchstens ${ARCTURIAN_RETURN_MAX_SPOKEN_CHARS} Zeichen und maximal drei Sätzen zusammen.`
      : `Gib diese Agenten-Rückmeldung knapp in höchstens ${ARCTURIAN_RETURN_MAX_SPOKEN_CHARS} Zeichen wieder.`;
  return { events: ordered, sourceEvents, instruction };
}

function eventNeedsSummary(event: ArcturianConversationEvent | undefined): boolean {
  return Boolean(event && event.content.length > ARCTURIAN_RETURN_MAX_SPOKEN_CHARS);
}
